/**
 * Schema Builder Hooks
 *
 * Hooks for managing user-defined database tables via the schema builder API.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type {
  SchemaStatus,
  ProcessSchemaConfig,
  CreateTableRequest,
  AlterTableRequest,
  AddColumnRequest,
  AlterColumnRequest,
  CreateRelationshipRequest,
  SchemaTableWithColumns,
  SchemaColumnResponse,
  SchemaRelationshipResponse,
  SchemaBuilderTable,
  SchemaBuilderColumn,
  SchemaBuilderRelationship,
  SimpleColumnType,
} from "../types";

const API_BASE = "/api/workspace/myvault/schema-builder";
const SCHEMAS_API_BASE = "/api/workspace/myvault/schemas";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Hook to get schema initialization status for a workspace.
 */
export const useSchemaStatus = (workspaceId: string) => {
  const [status, setStatus] = useState<SchemaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchStatus = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        `${API_BASE}/status`,
        "fetch schema status",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setStatus(response);
      setError(null);
    } catch (err) {
      console.error("Schema status fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch schema status";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    status,
    isInitialized: status?.is_initialized ?? false,
    loading,
    error,
    refresh: fetchStatus,
  };
};

/**
 * Hook for schema builder operations (initialize, tables, columns, relationships).
 */
export const useSchemaBuilder = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  /**
   * Initialize the process schema for the workspace.
   * Creates the database schema where user-defined tables will be stored.
   */
  const initializeSchema =
    useCallback(async (): Promise<ProcessSchemaConfig> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${API_BASE}/initialize`,
          "initialize schema",
          {
            method: "POST",
            headers: getWorkspaceHeaders(workspaceId),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to initialize schema";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [authFetch, workspaceId]);

  /**
   * Create a new table in the process schema.
   */
  const createTable = useCallback(
    async (data: CreateTableRequest): Promise<SchemaTableWithColumns> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(`${API_BASE}/tables`, "create table", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify(data),
        });
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create table";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Update table metadata.
   */
  const alterTable = useCallback(
    async (
      tableId: string,
      data: AlterTableRequest,
    ): Promise<SchemaTableWithColumns> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${API_BASE}/tables/${tableId}`,
          "alter table",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to alter table";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Drop a table from the process schema.
   */
  const dropTable = useCallback(
    async (tableId: string, force: boolean = false): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const url = new URL(
          `${API_BASE}/tables/${tableId}`,
          window.location.origin,
        );
        if (force) {
          url.searchParams.set("force", "true");
        }
        await authFetch(url.pathname + url.search, "drop table", {
          method: "DELETE",
          headers: getWorkspaceHeaders(workspaceId),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to drop table";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Add a column to an existing table.
   */
  const addColumn = useCallback(
    async (
      tableId: string,
      data: AddColumnRequest,
    ): Promise<SchemaColumnResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${API_BASE}/tables/${tableId}/columns`,
          "add column",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to add column";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Alter an existing column.
   */
  const alterColumn = useCallback(
    async (
      columnId: string,
      data: AlterColumnRequest,
    ): Promise<SchemaColumnResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${API_BASE}/columns/${columnId}`,
          "alter column",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to alter column";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Drop a column from a table.
   */
  const dropColumn = useCallback(
    async (columnId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(`${API_BASE}/columns/${columnId}`, "drop column", {
          method: "DELETE",
          headers: getWorkspaceHeaders(workspaceId),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to drop column";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Create a foreign key relationship between tables.
   */
  const createRelationship = useCallback(
    async (
      data: CreateRelationshipRequest,
    ): Promise<SchemaRelationshipResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${API_BASE}/relationships`,
          "create relationship",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create relationship";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Drop a foreign key relationship.
   */
  const dropRelationship = useCallback(
    async (relationshipId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(
          `${API_BASE}/relationships/${relationshipId}`,
          "drop relationship",
          {
            method: "DELETE",
            headers: getWorkspaceHeaders(workspaceId),
          },
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to drop relationship";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  /**
   * Map API data type to simple column type for UI display.
   */
  const mapDataTypeToSimple = (dataType: string): SimpleColumnType => {
    const upper = dataType.toUpperCase();
    if (
      upper.includes("DECIMAL") ||
      upper.includes("INT") ||
      upper.includes("FLOAT")
    ) {
      return "Number";
    }
    if (upper.includes("DATE") || upper.includes("TIME")) {
      return "Date";
    }
    if (upper.includes("BIT") || upper === "BOOLEAN") {
      return "Yes/No";
    }
    if (upper.includes("NVARCHAR(100)")) {
      // Selection type uses NVARCHAR(100)
      return "Selection";
    }
    return "Text";
  };

  /**
   * Fetch all existing tables with their columns from the backend.
   * Transforms API response to SchemaBuilderTable format.
   */
  const fetchExistingTables = useCallback(async (): Promise<{
    tables: SchemaBuilderTable[];
    relationships: SchemaBuilderRelationship[];
  }> => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all tables with columns in a single request
      const [tablesWithColumnsResponse, relationshipsResponse] =
        await Promise.all([
          authFetch(
            `${SCHEMAS_API_BASE}/tables/full`,
            "fetch tables with columns",
            { headers: getWorkspaceHeaders(workspaceId) },
          ),
          authFetch(
            `${SCHEMAS_API_BASE}/relationships`,
            "fetch relationships",
            { headers: getWorkspaceHeaders(workspaceId) },
          ),
        ]);

      const tables: SchemaBuilderTable[] = tablesWithColumnsResponse.map(
        (table: SchemaTableWithColumns) => {
          const mappedColumns: SchemaBuilderColumn[] = (
            table.columns || []
          ).map((col: SchemaColumnResponse) => ({
            tempId: col.id,
            id: col.id,
            name: col.column_name,
            displayName: col.display_name,
            dataType: mapDataTypeToSimple(col.data_type),
            isPrimaryKey: col.is_primary_key,
            isRequired: !col.is_nullable,
            sortOrder: 0,
          }));

          // Sort: PK first, then others
          const pkColumns = mappedColumns.filter(
            (c: SchemaBuilderColumn) => c.isPrimaryKey,
          );
          const otherColumns = mappedColumns.filter(
            (c: SchemaBuilderColumn) => !c.isPrimaryKey,
          );
          const columns: SchemaBuilderColumn[] = [
            ...pkColumns.map((c: SchemaBuilderColumn, i: number) => ({
              ...c,
              sortOrder: i,
            })),
            ...otherColumns.map((c: SchemaBuilderColumn, i: number) => ({
              ...c,
              sortOrder: pkColumns.length + i,
            })),
          ];

          return {
            tempId: table.id,
            id: table.id,
            name: table.table_name,
            displayName: table.display_name,
            description: table.description || "",
            columns,
          };
        },
      );

      const relationships: SchemaBuilderRelationship[] =
        relationshipsResponse.map((rel: SchemaRelationshipResponse) => ({
          tempId: rel.id,
          id: rel.id,
          sourceTableId: rel.from_table_id,
          sourceColumnId: rel.from_column_id,
          targetTableId: rel.to_table_id,
          targetColumnId: rel.to_column_id,
          displayName: rel.relationship_name,
          onDelete: rel.on_delete || "RESTRICT",
        }));

      return { tables, relationships };
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch existing tables";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authFetch, workspaceId]);

  return {
    initializeSchema,
    createTable,
    alterTable,
    dropTable,
    addColumn,
    alterColumn,
    dropColumn,
    createRelationship,
    dropRelationship,
    fetchExistingTables,
    loading,
    error,
  };
};
